import { Action, createReducer, on } from '@ngrx/store';
import { IActiveParsing, PARSE_STATE } from '../../../interfaces/cv-parse';
import * as CvParseActions from '../../actions/cv-parse.actions';

export interface CvParseState {
  activeParsing: IActiveParsing;
  parsingState: PARSE_STATE;
  uploadFile: {
    error: boolean;
    loading: boolean;
  };
  parsingResult: {
    cvId: number;
    error: boolean;
    loading: boolean;
  };
  cancelParsing: {
    error: boolean;
    loading: boolean;
  };
  ribbon: {
    folded: boolean,
    show: boolean,
  };
}

export const initialState: CvParseState = {
  activeParsing: {
    requestId: null,
    cvId: null,
    loading: false,
    error: false
  },
  uploadFile: {
    error: false,
    loading: false,
  },
  parsingState: null,
  parsingResult: {
    cvId: null,
    error: false,
    loading: false,
  },
  cancelParsing: {
    error: false,
    loading: false,
  },
  ribbon: {
    folded: true,
    show: false,
  }
};

const reducer = createReducer(
  initialState,
  on(CvParseActions.checkActiveParsing, (state) => ({
      ...state,
      activeParsing: {
        ...state.activeParsing,
        loading: true,
        error: false
      }
  })),
  on(CvParseActions.checkActiveParsingSuccess, (state, { activeParsing }) => ({
      ...state,
      activeParsing: {
        requestId: activeParsing.RequestId,
        cvId: activeParsing.CvId,
        loading: false,
        error: false
      }
  })),
  on(CvParseActions.noActiveParsing, (state) => ({
      ...state,
      activeParsing: {
        requestId: null,
        cvId: null,
        loading: false,
        error: false
      }
  })),
  on(CvParseActions.checkActiveParsingError, (state) => ({
      ...state,
      activeParsing: {
        ...state.activeParsing,
        loading: false,
        error: true
      }
  })),
  on(CvParseActions.cancelActiveParsing, (state) => ({
      ...state,
      cancelParsing: {
        error: false,
        loading: true,
      }
  })),
  on(CvParseActions.cancelActiveParsingSuccess, (state) => ({
      ...state,
      parsingState: PARSE_STATE.CANCELLED,
      activeParsing: {
        requestId: null,
        cvId: null,
        loading: false,
        error: false
      },
      cancelParsing: {
        error: false,
        loading: false,
      }
  })),
  on(CvParseActions.cancelActiveParsingError, (state) => ({
      ...state,
      cancelParsing: {
        error: true,
        loading: false,
      }
  })),
  on(CvParseActions.uploadFileToParse, (state) => ({
      ...state,
      parsingState: PARSE_STATE.UPLOADING,
      uploadFile: {
        error: false,
        loading: true,
      }
  })),
  on(CvParseActions.uploadFileToParseSuccess, (state, { activeParsing }) => ({
      ...state,
      activeParsing: {
        requestId: activeParsing.RequestId,
        cvId: activeParsing.CvId,
        loading: false,
        error: false
      },
      uploadFile: {
        error: false,
        loading: false,
      }
  })),
  on(CvParseActions.uploadFileToParseError, (state) => ({
      ...state,
      parsingState: PARSE_STATE.PARSED_ERROR,
      uploadFile: {
        error: true,
        loading: false,
      }
  })),
  on(CvParseActions.getParsingResultForId, (state) => ({
      ...state,
      parsingState: PARSE_STATE.PARSING,
      parsingResult: {
        ...state.parsingResult,
        loading: true,
        error: false,
      }
  })),
  on(CvParseActions.getParsingResultForIdSuccess, (state, { cvId }) => ({
      ...state,
      activeParsing: {
        ...state.activeParsing,
        requestId: null,
        cvId: null,
      },
      parsingState: PARSE_STATE.PARSED_SUCCESS,
      parsingResult: {
        cvId,
        error: false,
        loading: false,
      }
  })),
  on(CvParseActions.getParsingResultForIdError, (state) => ({
      ...state,
      activeParsing: {
        ...state.activeParsing,
        requestId: null,
        cvId: null,
      },
      parsingState: PARSE_STATE.PARSED_ERROR,
      parsingResult: {
        cvId: null,
        error: true,
        loading: false,
      }
  })),
  on(CvParseActions.resetParsingState, (state) => ({
      ...state,
      parsingState: null,
      parsingResult: {
        cvId: null,
        error: false,
        loading: false,
      },
      ribbon: {
        folded: true,
        show: false,
      }
  })),
  on(CvParseActions.updateRibbonState, (state, { folded, show }) => ({
      ...state,
      ribbon: {
        folded,
        show,
      }
  })),
  on(CvParseActions.updateRibbonShowState, (state, { show }) => ({
      ...state,
      ribbon: {
        ...state.ribbon,
        show,
      }
  })),
  on(CvParseActions.updateRibbonFoldedState, (state, { folded }) => ({
      ...state,
      ribbon: {
        ...state.ribbon,
        folded,
      }
  }))
);

export function cvParseReducer(
  state: CvParseState | undefined,
  action: Action
) {
  return reducer(state, action);
}

export const getActiveParsingStatus = (state: CvParseState) => {
  return state.activeParsing;
};

export const getParsingState = (state: CvParseState) => {
  return state.parsingState;
};

export const getUploadFileStatus = (state: CvParseState) => {
  return state.uploadFile;
};

export const getParsingResultStatus = (state: CvParseState) => {
  return state.parsingResult;
};

export const getCancelParsingStatus = (state: CvParseState) => {
  return state.cancelParsing;
};

export const getRibbonState = (state: CvParseState) => {
  return state.ribbon;
}
